import { useEffect, useState } from 'react'
import { useRouter } from 'next/router'

import { Parking, TagsTypes } from '@/service'

import { Market } from '@/utils/multi-markets/types'
import { Locale } from '@/utils/i18n/types'

import { useFetchPaginationParkings } from '@/service/apiHooks/useFetchPaginationParkings'

import { ServicesValuesTypes } from '../components/Filters/Services/Services.types'

export const useFetchAndFilterParkings = (
  queryParams: {
    countryCode: Market
    visible: boolean
    size: number
    sorting: string
    radius: number | undefined
    lang: Locale
    page: number
  },
  selectedServices: ServicesValuesTypes[],
  currency: string,
  setCurrency: (currency: string) => void,
  setParkingsCount?: (val: number) => void,
  priceRange?: { min: number; max: number }
) => {
  const router = useRouter()
  const { service } = router.query

  const extendedParams = {
    ...queryParams,
    ...(service === 'PARK_AND_FLY' && { infoTag: 'PARK_AND_FLY' }),
    size: 150
  }

  const { parkings, isLoading } = useFetchPaginationParkings({
    params: extendedParams
  })
  const [filteredParkings, setFilteredParkings] = useState<Parking[]>([])

  useEffect(() => {
    if (!parkings) return

    let newFilteredParkings = parkings

    if (selectedServices.length > 0) {
      newFilteredParkings = newFilteredParkings.filter(parking =>
        selectedServices.every(service =>
          parking.infoTags?.includes(service as TagsTypes)
        )
      )
    }

    if (priceRange) {
      newFilteredParkings = newFilteredParkings.filter(parking => {
        const pricePerHour = parking.price?.pricePerHour?.amount || 0
        const pricePer24Hours = parking.price?.pricePer24Hours?.amount || 0
        const price = pricePerHour > 0 ? pricePerHour : pricePer24Hours
        return price >= priceRange.min && price <= priceRange.max
      })
    }

    if (!currency && newFilteredParkings.length > 0) {
      const defaultCurrency =
        newFilteredParkings[0]?.price?.pricePerHour?.currency ||
        newFilteredParkings[0]?.price?.pricePer24Hours?.currency
      if (defaultCurrency && defaultCurrency !== currency) {
        setCurrency(defaultCurrency)
      }
    }

    const filteredJson = JSON.stringify(newFilteredParkings)
    setFilteredParkings(prev => {
      if (JSON.stringify(prev) !== filteredJson) {
        setParkingsCount?.(newFilteredParkings.length)
        return newFilteredParkings
      }
      return prev
    })
  }, [
    parkings,
    selectedServices,
    priceRange,
    currency,
    setCurrency,
    setParkingsCount
  ])

  return { filteredParkings, isLoading }
}
